import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { FormDialog } from "@/components/organisms/FormDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useReauthStore } from "@/lib/stores/reauth";
import { useAuthStore } from "@/lib/stores/auth";
import { resetReauthPending } from "@/lib/api";
import { storeAuth, signOut } from "@/lib/auth";
import { tokenStorage } from "@/lib/storage-tokens";
import { getErrorMessage } from "@/lib/errors";
import { trackSignedIn } from "@/lib/analytics";
import { ErrorBanner, SuccessBanner } from "./FormFeedback";
import { GoogleIcon, MicrosoftIcon } from "./OAuthIcons";
import { OtpCodeInput } from "./OtpCodeInput";
import {
  useRequestMagicLinkMutation,
  useVerifyMagicCodeMutation,
} from "./queries";
import { persistReturnRoute, buildOAuthUrl } from "./reauth-helpers";
import { navigateToCompleteProfile } from "./auth-helpers";
import { AUTH_COPY } from "./auth-copy";
import { useTurnstile } from "./use-turnstile";

type Step = "idle" | "code";

/**
 * Seamless re-auth modal (TRU-301). Mounted once at the app shell. Opens (via
 * `useReauthStore`) when `lib/api.ts` hits a terminal 401, instead of redirecting
 * to /login. Re-authenticates in place — the route is never left, the magic-link
 * code is entered right here, and on success the workspace is restored exactly.
 * Email is read-only: re-auth is the same account; switching accounts is "Sign out".
 */
export function ReAuthModal() {
  const navigate = useNavigate();
  const isOpen = useReauthStore((s) => s.isOpen);
  const close = useReauthStore((s) => s.close);
  const email = useAuthStore((s) => s.user?.email) ?? "";
  const workspaceId = useAuthStore((s) => s.workspace?.id);

  const [step, setStep] = useState<Step>("idle");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const requestMutation = useRequestMagicLinkMutation();
  const verifyMutation = useVerifyMagicCodeMutation();
  const loading = requestMutation.isPending || verifyMutation.isPending;
  const captcha = useTurnstile(email);

  function reset() {
    setStep("idle");
    setCode("");
    setError(null);
    setSuccess(null);
  }

  async function handleSignOut() {
    resetReauthPending();
    close();
    reset();
    await signOut();
    void navigate({ to: "/login", replace: true });
  }

  async function handleSendCode() {
    setError(null);
    setSuccess(null);
    try {
      await requestMutation.mutateAsync({
        email,
        captchaToken: captcha.getToken(),
      });
      setStep("code");
      setSuccess(AUTH_COPY.codeSent(email));
    } catch (err) {
      // Tokens are single-use — the retry needs a fresh one.
      captcha.reset();
      setError(
        getErrorMessage(err, "Could not send a code. Please try again."),
      );
    }
  }

  async function handleVerify(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const result = await verifyMutation.mutateAsync({
        email,
        code,
        workspaceId,
        // Carry the user's original "Keep me signed in" answer into the new
        // session. A session expiring isn't the user changing their mind, so
        // re-auth must not silently demote a remembered login to a short one.
        // Re-setting the mode to what it already is, is a no-op.
        rememberMe: tokenStorage.getPersistence() === "persistent",
      });
      resetReauthPending();
      close();
      reset();
      if (navigateToCompleteProfile(navigate, result)) return;
      storeAuth(result);
      trackSignedIn(result.user.email, "reauth_modal");
    } catch (err) {
      setError(getErrorMessage(err, AUTH_COPY.verifyFailed));
    }
  }

  function handleSocial(provider: "google" | "microsoft") {
    // OAuth is a full-page redirect; stash the route so the callback restores it.
    persistReturnRoute(workspaceId);
    // Deliberate full-page navigation to an external OAuth authorize URL — not
    // internal routing, so useNavigate()/<Link> don't apply here.
    window.location.href = buildOAuthUrl(provider, workspaceId);
  }

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) void handleSignOut();
      }}
      title="Your session expired"
      description={
        <>
          Sign back in as <strong>{email}</strong> to pick up right where you
          left off.
        </>
      }
    >
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {success && <SuccessBanner>{success}</SuccessBanner>}

      {step === "idle" ? (
        <div className="flex flex-col gap-4">
          {captcha.enabled && <div ref={captcha.containerRef} />}

          <Button
            className="w-full"
            disabled={loading}
            onClick={() => void handleSendCode()}
          >
            {requestMutation.isPending ? "Sending…" : "Email me a sign-in code"}
          </Button>

          <div className="flex items-center gap-2">
            <Separator className="flex-1" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => handleSocial("google")}
            >
              <GoogleIcon />
              Continue with Google
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => handleSocial("microsoft")}
            >
              <MicrosoftIcon />
              Continue with Microsoft
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => void handleVerify(e)}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reauth-code">Verification code</Label>
            <OtpCodeInput
              id="reauth-code"
              value={code}
              onChange={setCode}
              required
              autoFocus
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={loading || code.length !== 6}
          >
            {verifyMutation.isPending ? "Verifying…" : "Verify & continue"}
          </Button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-primary disabled:opacity-50"
            disabled={loading}
            onClick={() => {
              setStep("idle");
              setCode("");
              setError(null);
              setSuccess(null);
            }}
          >
            ← Back
          </button>
        </form>
      )}

      <button
        type="button"
        className="text-sm text-muted-foreground hover:text-primary self-center"
        onClick={() => void handleSignOut()}
      >
        Sign out instead
      </button>
    </FormDialog>
  );
}
